import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiTrash } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { isEmpty, isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import QuestionCircle from '../../components/misc/QuestionCircle';
import DeleteCommunityModal from '../../components/modals/DeleteCommunityModal';
import UpgradeModal from '../../components/modals/UpgradeModal';
import DisableMemberAdministrationModal from '../../components/modals/DisableMemberAdministrationModal';
import communityService from '../../../services/community/communityService';
import businessService from '../../../services/business/businessService';
import inviteService from '../../../services/community/inviteService';
import { CommunityRole, CommunityMemberPermissions } from '../../../types/community';
import { BusinessRole } from '../../../types/business';
import Button from '../../components/buttons/Button';
import ImageInput from '../../components/forms/ImageInput';
import useCommunityEffect from '../../../hooks/effects/useCommunityEffect';
import PageHeader from '../../components/misc/PageHeader';
import FeaturesForm from '../../components/misc/FeaturesForm';
import MemberPreferenceForm, {
  getDefaultMemberPermissions,
} from '../../components/misc/MemberPreferenceForm';
import StorageUsage from '../../components/misc/StorageUsage';
import { CommunityFeatures, getMinimumSubscriptionForFeatures, BusinessFeatures, getBusinessSettingsFeatures } from '../../../utils/FeatureUtils';
import { getMinimumBusinessSubscriptionForFeatures, isBusinessSubscriptionGreaterThanOrEqual } from '../../../utils/BusinessFeatureUtils';
import { SubscriptionType, BusinessSubscriptionType } from '../../../types/misc';
import { CommunityInvitePropertyType, CommunityInvite } from '../../../models/inviteService.model';

export default function GeneralSettingsPage(): JSX.Element {
  const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';
  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Use business or community data based on environment
  const entity = isBusiness ? business : community;
  const entityName = entity?.name || '';
  const entityDescription = entity?.description || '';
  const entityPicture = entity?.picture || undefined;
  const entityFeatures = isBusiness
    ? getBusinessSettingsFeatures(business)
    : (community?.features || {} as CommunityFeatures);

  const [activeTab, setActiveTab] = React.useState<'general' | 'features' | 'memberPreferences'>(
    location.state?.view ? location.state.view : 'general'
  );
  const [deleteDialog, setDeleteDialog] = React.useState<boolean>(false);
  const [upgradeModal, setUpgradeModal] = React.useState<{
    open: boolean;
    requiredSubscription: SubscriptionType | BusinessSubscriptionType;
    featureName: string;
  }>({
    open: false,
    requiredSubscription: isBusiness ? BusinessSubscriptionType.STARTER : SubscriptionType.FREE,
    featureName: '',
  });
  const [showDisableModal, setShowDisableModal] = React.useState(false);
  const [currentInvite, setCurrentInvite] = React.useState<CommunityInvite | undefined>();
  const [name, setName] = React.useState<string>(entityName);
  const [features, setFeatures] = React.useState<CommunityFeatures | BusinessFeatures>(entityFeatures);
  const [memberPreferences, setMemberPreferences] = React.useState<CommunityMemberPermissions>(
    isEmpty(community?.memberPermissions)
      ? getDefaultMemberPermissions()
      : community!.memberPermissions,
  );
  const [description, setDescription] = React.useState<string>(entityDescription);
  const [profileImageUrl, setProfileImageUrl] = React.useState<string | undefined>(
    entityPicture,
  );
  const [profileImageAction, setProfileImageAction] = React.useState<'UPLOAD' | 'DELETE'>();

  useCommunityEffect(() => {
    if (isBusiness) {
      setName(business?.name || '');
      setDescription(business?.description || '');
      setProfileImageUrl(business?.picture || undefined);
      setFeatures(getBusinessSettingsFeatures(business));
    } else {
      setName(community?.name || '');
      setDescription(community?.description || '');
      setProfileImageUrl(community?.picture || undefined);
      setFeatures(community?.features || {});
    }
  }, [isBusiness, business, community]);

  const getUpdateRequiredFeature = (): string => {
    if (isBusiness) {
      const businessFeats = features as BusinessFeatures;
      const currentBusinessFeats = getBusinessSettingsFeatures(business);
      const newFeatures = Object.keys(businessFeats).filter(
        (key) => businessFeats[key as keyof BusinessFeatures] && !currentBusinessFeats[key as keyof BusinessFeatures],
      );

      return newFeatures.length > 0 ?
        newFeatures[0].split('_')
          .map((word, index) =>
            index === 0
              ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              : word.toLowerCase()
          )
          .join(' ') : '';
    }

    const communityFeats = features as CommunityFeatures;
    const newFeatures = Object.keys(communityFeats).filter(
      (key) => communityFeats[key as keyof CommunityFeatures] && !community?.features?.[key as keyof CommunityFeatures],
    );

    return newFeatures.length > 0 ?
      newFeatures[0].split('_')
        .map((word, index) =>
          index === 0
            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            : word.toLowerCase()
        )
        .join(' ') : '';
  };

  const handleSave = async (): Promise<void> => {
    try {
      // Only check for member administration for community
      if (!isBusiness && community?.features?.MEMBER_ADMINISTRATION && !(features as CommunityFeatures).MEMBER_ADMINISTRATION) {
        try {
          const fields = await inviteService.fetchInvite();
          if (fields && fields.nonRequestedFields.length !== 10) {
            setCurrentInvite(fields);
            setShowDisableModal(true);
            return;
          }
        } catch {
          toast.error(t('toast.error.memberAdministrationFeature'));
          return;
        }
      }

      await performSave();
    } catch {
      toast.error(t('toast.error.imageWrong'));
      setProfileImageAction(undefined);
    }
  };

  const performSave = async (): Promise<void> => {
    try {
      if (isBusiness) {
        const businessFeats = features as BusinessFeatures;
        const currentFeatures = getBusinessSettingsFeatures(business);
        const newlyEnabledFeatures = Object.fromEntries(
          Object.entries(businessFeats).filter(([key, value]) => value && !currentFeatures[key as keyof BusinessFeatures]),
        ) as BusinessFeatures;

        if (Object.keys(newlyEnabledFeatures).length > 0) {
          let currentSubscription: BusinessSubscriptionType;
          if (!business) {
            currentSubscription = BusinessSubscriptionType.STARTER;
          } else if (business.trialEndDate && new Date(business.trialEndDate) > new Date()) {
            currentSubscription = business.trialType || BusinessSubscriptionType.STARTER;
          } else {
            currentSubscription = business.subscription?.type ?? BusinessSubscriptionType.STARTER;
          }
          const requiredSubscription = getMinimumBusinessSubscriptionForFeatures(newlyEnabledFeatures);

          if (!isBusinessSubscriptionGreaterThanOrEqual(currentSubscription, requiredSubscription)) {
            toast.info(
              t('component.modal.upgrade.description', {
                feature: getUpdateRequiredFeature(),
                plan: 'Professional',
              }),
            );
            return;
          }
        }

        await businessService.updateBusiness(name, description, businessFeats);

        if (profileImageAction === 'UPLOAD' && profileImageUrl) {
          await businessService.setProfileImage(
            await fetch(profileImageUrl!).then((res) => res.blob() as Promise<File>),
          );
        } else if (profileImageAction === 'DELETE') {
          await businessService.deleteProfileImage();
        }

        toast.success(t('toast.success.communitySaved'));
        setProfileImageAction(undefined);
      } else {
        // Community save logic
        const communityFeats = features as CommunityFeatures;

        if (communityFeats !== community?.features) {
          let currentSubscription;
          if (!community) {
            currentSubscription = SubscriptionType.FREE;
          } else if (community.trialEndDate && new Date(community.trialEndDate) > new Date()) {
            currentSubscription = community.trialType || SubscriptionType.FREE;
          } else {
            currentSubscription = community.subscription?.type ?? SubscriptionType.FREE;
          }
          const requiredSubscription = getMinimumSubscriptionForFeatures(communityFeats);

          if (requiredSubscription === SubscriptionType.BASE && currentSubscription === SubscriptionType.FREE) {
            setUpgradeModal({
              open: true,
              requiredSubscription: SubscriptionType.BASE,
              featureName: getUpdateRequiredFeature(),
            });
            return;
          }

          if (requiredSubscription === SubscriptionType.PRO && currentSubscription !== SubscriptionType.PRO) {
            setUpgradeModal({
              open: true,
              requiredSubscription: SubscriptionType.PRO,
              featureName: getUpdateRequiredFeature(),
            });
            return;
          }
        }

        await communityService.updateCommunity(name, description, memberPreferences, communityFeats);

        if (communityFeats.MEMBER_ADMINISTRATION && !community?.features?.MEMBER_ADMINISTRATION) {
          try {
            const inviteData = await inviteService.fetchInvite();

            const emailAlreadyRequested =
              inviteData.mandatoryFields.includes(CommunityInvitePropertyType.EMAIL) ||
              inviteData.optionalFields.includes(CommunityInvitePropertyType.EMAIL);

            if (!emailAlreadyRequested) {
              const updatedInvite = {
                ...inviteData,
                optionalFields: [...inviteData.optionalFields, CommunityInvitePropertyType.EMAIL],
                nonRequestedFields: inviteData.nonRequestedFields.filter(field => field !== CommunityInvitePropertyType.EMAIL)
              };

              await inviteService.updateInvite(updatedInvite);
            }
          } catch (error) {
            console.error('Failed to update invite with EMAIL field:', error);
          }
        }

        if (profileImageAction === 'UPLOAD' && profileImageUrl) {
          await communityService.setProfileImage(
            await fetch(profileImageUrl!).then((res) => res.blob() as Promise<File>),
          );
        } else if (profileImageAction === 'DELETE') {
          await communityService.deleteProfileImage();
        }

        toast.success(t('toast.success.communitySaved'));
        setProfileImageAction(undefined);
      }
    } catch {
      toast.error(t('toast.error.imageWrong'));
      setProfileImageAction(undefined);
    }
  };

  const handleDisableMemberAdministration = async () => {
    if (currentInvite) {
      try {
        const allFields = [...currentInvite.mandatoryFields, ...currentInvite.optionalFields];
        const updatedInvite = {
          ...currentInvite,
          mandatoryFields: [],
          optionalFields: [],
          nonRequestedFields: [...currentInvite.nonRequestedFields, ...allFields],
          customFields: [],
        };

        await inviteService.updateInvite(updatedInvite);
      } catch (error) {
        console.error('Failed to clear invite data:', error);
        toast.error(t('toast.error.failedToClearData'));
        return;
      }
    }

    await performSave();
  };

  const handleUpgrade = () => {
    navigate('/settings/subscriptions');
  };

  const isUnchanged = isBusiness
    ? description === business?.description &&
    name === business?.name &&
    !profileImageAction &&
    isEqual(features, getBusinessSettingsFeatures(business))
    : description === community?.description &&
    name === community?.name &&
    !profileImageAction &&
    features === community?.features &&
    memberPreferences === community?.memberPermissions;

  return (
    <>
      <div>
        <PageHeader
          title={t('page.settings.general.title')}
          subtitle={t('page.settings.subtitle', { type: 'community' })}
        />

        {/* Tabs */}
        <div className="flex gap-4 border-b border-primary-300">
          {(['general', 'features', ...(!isBusiness ? (['memberPreferences'] as const) : [])] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setActiveTab(tabKey as any)}
              className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${activeTab === tabKey
                  ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                  : 'text-primary-600 hover:text-primary-800'
                }`}>
              {t(`page.settings.general.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        <div className="mb-8 flex flex-col gap-6 lg:flex-row">
          {/* Left: Only for General tab */}
          {activeTab === 'general' && (
            <div className="w-full pt-8 lg:w-1/2 lg:py-8">
              <div className="flex items-center">
                <h2 className="mb-2 mr-1 mt-1 text-xl font-medium">
                  {t('page.settings.general.image.title')}
                </h2>
                <QuestionCircle className="-left-10 top-7 w-[300px]">
                  {t('page.settings.general.image.explanation')}
                </QuestionCircle>
              </div>
              <ImageInput
                className="h-64 lg:h-72"
                iconClassName="w-14 h-14"
                image={profileImageUrl}
                setImage={setProfileImageUrl}
                setImageAction={setProfileImageAction}
              />
            </div>
          )}

          {/* Right section */}
          <div className="w-full py-8 lg:flex-1">
            {activeTab === 'general' && (
              <>
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.generalData.title')}
                </h2>

                <div className="flex flex-col py-4">
                  <label className="-mt-4 pb-1 pl-1 pr-2 font-medium">{t('general.name')}</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    type="text"
                    placeholder={t('page.settings.general.generalData.namePlaceholder')}
                  />
                </div>

                <div className="flex flex-col py-4">
                  <label className="-mt-4 pb-1 pl-1 pr-2 font-medium">
                    {t('general.description')}
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="h-28"
                    placeholder={t('page.settings.general.generalData.descriptionPlaceholder')}
                  />
                </div>
              </>
            )}

            {activeTab === 'features' && (
              <div className="w-2/3">
                {/* Storage Usage - Only for community */}
                {!isBusiness && (
                  <>
                    <h2 className="mb-2 mt-1 text-xl font-medium">
                      {t('page.settings.general.tabs.storage')}
                    </h2>
                    <div className="mb-4">
                      <StorageUsage />
                    </div>
                  </>
                )}

                {/* Features */}
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.tabs.features')}
                </h2>
                <FeaturesForm features={features} onFeaturesChange={setFeatures} isBusiness={isBusiness} />
              </div>
            )}

            {activeTab === 'memberPreferences' && !isBusiness && (
              <div className="w-full pt-8 lg:w-1/2 lg:py-8">
                <h2 className="mb-2 mt-1 text-xl font-medium">
                  {t('page.settings.general.tabs.memberPermissions')}
                </h2>
                <MemberPreferenceForm
                  disableEventPreference={!(features as CommunityFeatures).EVENTS}
                  disableScoreCardPreference={!(features as CommunityFeatures).STATS_AND_SCORES}
                  disableTaskPreference={!(features as CommunityFeatures).TASKS}
                  disablePhotoAlbumPreference={!(features as CommunityFeatures).PHOTO_ALBUMS}
                  disableWallPreference={!(features as CommunityFeatures).WALL}
                  initialMemberPermissions={memberPreferences}
                  onMemberPermissionsChange={setMemberPreferences}
                />
              </div>
            )}

            <div className="mt-10 flex justify-end">
              <Button
                variant="primary"
                onClick={handleSave}
                className="px-8"
                disabled={isUnchanged || !name}>
                {t('general.save')}
              </Button>
            </div>
          </div>
        </div>

        {/* Delete Community/Business */}
        {((!isBusiness && community?.role === CommunityRole.ADMIN) ||
          (isBusiness && business?.role === BusinessRole.OWNER)) && (
            <div className="flex w-full justify-end">
              <Button onClick={() => setDeleteDialog(true)} className="underline">
                {t('page.settings.general.delete')}
                <HiTrash className="ml-1 h-5 w-5" />
              </Button>
            </div>
          )}
      </div>

      <DeleteCommunityModal open={deleteDialog} setOpen={setDeleteDialog} />
      {!isBusiness && (
        <UpgradeModal
          open={upgradeModal.open}
          setOpen={(open) => setUpgradeModal(prev => ({ ...prev, open }))}
          requiredSubscription={upgradeModal.requiredSubscription as SubscriptionType}
          featureName={upgradeModal.featureName}
          onUpgrade={handleUpgrade}
        />
      )}
      <DisableMemberAdministrationModal
        open={showDisableModal}
        setOpen={setShowDisableModal}
        currentInvite={currentInvite}
        onConfirmDisable={handleDisableMemberAdministration}
      />
    </>
  );
}
